
import React, { useState } from 'react';
import { UserProfile } from '../../../app/domain/types/index.ts';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { Calendar, Download, TrendingUp, TrendingDown, Activity, Target, Share2 } from 'lucide-react';

interface AnalyticsReportingProps {
  user: UserProfile;
}

const AnalyticsReporting: React.FC<AnalyticsReportingProps> = ({ user }) => {
  const [timeRange, setTimeRange] = useState('1M');

  // Mock Data
  const weightData = [
    { date: 'Week 1', weight: 88 },
    { date: 'Week 2', weight: 87.2 },
    { date: 'Week 3', weight: 86.5 },
    { date: 'Week 4', weight: 85.8 },
    { date: 'Week 5', weight: 85.5 },
  ];

  const activityData = [
    { day: 'Mon', minutes: 45 },
    { day: 'Tue', minutes: 30 },
    { day: 'Wed', minutes: 60 },
    { day: 'Thu', minutes: 45 },
    { day: 'Fri', minutes: 50 },
    { day: 'Sat', minutes: 90 },
    { day: 'Sun', minutes: 20 },
  ];

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Header */}
      <div className="p-6 md:p-8 pb-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md sticky top-0 z-20">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Progress Analytics</h1>
          <div className="flex gap-2">
            <button className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors">
              <Share2 size={20} />
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors">
              <Download size={16} /> Export Report
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          {['1W', '1M', '3M', '6M', '1Y'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${
                timeRange === range 
                ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' 
                : 'bg-stone-100 dark:bg-stone-800 text-gray-500 dark:text-gray-400 hover:bg-stone-200 dark:hover:bg-stone-700'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="flex-1 overflow-y-auto p-6 md:p-8 no-scrollbar">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="p-6 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <p className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Weight Lost</p>
              <span className="text-green-500 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
                <TrendingDown size={12} /> 2.8%
              </span>
            </div>
            <h3 className="text-4xl font-extrabold text-gray-900 dark:text-white">2.5 <span className="text-lg font-medium text-gray-400">kg</span></h3>
            <p className="text-xs text-gray-400 mt-2">Start: 88kg • Current: 85.5kg</p>
          </div>

          <div className="p-6 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <p className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Goal Progress</p>
              <span className="text-blue-500 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
                <Target size={12} /> On Track
              </span>
            </div>
            <h3 className="text-4xl font-extrabold text-gray-900 dark:text-white">45<span className="text-lg font-medium text-gray-400">%</span></h3>
            <div className="w-full bg-stone-100 dark:bg-stone-700 h-1.5 rounded-full mt-3 overflow-hidden">
              <div className="bg-blue-500 h-full w-[45%] rounded-full"></div>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <p className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Activity Streak</p>
              <span className="text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
                <Activity size={12} /> High
              </span>
            </div>
            <h3 className="text-4xl font-extrabold text-gray-900 dark:text-white">12 <span className="text-lg font-medium text-gray-400">days</span></h3>
            <p className="text-xs text-gray-400 mt-2">Personal Best: 15 days</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

          {/* Weight Chart */}
          <div className="p-6 rounded-3xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Weight Trend</h3>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weightData}>
                  <defs>
                    <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2E7D32" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#2E7D32" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" opacity={0.5} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                  <YAxis domain={['dataMin - 1', 'dataMax + 1']} axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                  <Tooltip contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'}} />
                  <Area type="monotone" dataKey="weight" stroke="#2E7D32" strokeWidth={3} fill="url(#colorWeight)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Activity Chart */}
          <div className="p-6 rounded-3xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Weekly Activity</h3>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" opacity={0.5} />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                  <Tooltip cursor={{fill: 'transparent'}} contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'}} />
                  <Bar dataKey="minutes" radius={[6, 6, 0, 0]}>
                    {activityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.minutes >= 60 ? '#F57C00' : '#F57C0080'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Detailed Insights Table */}
        <div className="p-6 rounded-3xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Detailed Metrics</h3>
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="border-b border-stone-100 dark:border-stone-700">
              <tr>
                <th className="pb-3 font-bold text-gray-400 uppercase text-xs">Metric</th>
                <th className="pb-3 font-bold text-gray-400 uppercase text-xs">Current</th>
                <th className="pb-3 font-bold text-gray-400 uppercase text-xs">Goal</th>
                <th className="pb-3 font-bold text-gray-400 uppercase text-xs text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-700">
              {[
                { metric: 'BMI', current: '26.3', goal: '24.0', status: 'Improving' },
                { metric: 'Daily Water', current: '1.8L', goal: '2.5L', status: 'Needs Work' },
                { metric: 'Sleep Avg', current: '6.5h', goal: '8.0h', status: 'Needs Work' },
                { metric: 'Protein Intake', current: '110g', goal: '120g', status: 'Excellent' },
              ].map((row, i) => (
                <tr key={i}>
                  <td className="py-4 font-medium text-gray-900 dark:text-white">{row.metric}</td>
                  <td className="py-4">{row.current}</td>
                  <td className="py-4">{row.goal}</td>
                  <td className="py-4 text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      row.status === 'Excellent' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      row.status === 'Improving' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default AnalyticsReporting;
